import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from './firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  isAdmin: boolean;
  accessError: string | null;
  refreshAccess: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  googleSignIn: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const accessRequest = useRef(0);

  const checkAccess = useCallback(async (user: User | null) => {
    const request = ++accessRequest.current;
    setCurrentUser(user);
    setIsAdmin(false);
    setAccessError(null);
    setLoading(!!user);
    if (!user) return;
    try {
      // Existing browser sessions can retain claims from before an admin grant.
      // Refresh before mounting listeners or calling privileged functions.
      const token = await user.getIdTokenResult(true);
      if (request === accessRequest.current) setIsAdmin(token.claims.admin === true);
    } catch {
      if (request === accessRequest.current) {
        setAccessError('We could not verify your admin access. Check your connection and try again.');
      }
    } finally {
      if (request === accessRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, checkAccess);
    return () => {
      unsubscribe();
      ++accessRequest.current;
    };
  }, [checkAccess]);

  const refreshAccess = useCallback(() => checkAccess(auth.currentUser), [checkAccess]);
  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };
  const googleSignIn = async () => {
    await signInWithPopup(auth, new GoogleAuthProvider());
  };
  const logout = async () => { await signOut(auth); };

  return (
    <AuthContext.Provider value={{ currentUser, loading, isAdmin, accessError, refreshAccess, login, googleSignIn, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
